// ============================================================================
// Die Anmeldung (E2, Anmeldung.dc.html). Zwei Schritte: Adresse → Code.
// Portiert aus /opt/lilasp-zeitplan/frontend/src/screens/Anmelden.tsx, dort
// aus dem Fragenbüro — samt der vier Fallen, die dort schon zugeschlagen
// haben. KEINE davon wegräumen:
//
// (a) Der Doppelabsende-Riegel liegt in einem REF, nicht im Zustand: das
//     Autofill des Telefons löst `submit` UND `click` im selben Tick aus, und
//     ein Zustandswert wäre beim zweiten Aufruf noch der alte. Ein Code gilt
//     EINMAL — der zweite Versuch liefe ins Leere, und die Person läse „Der
//     Code stimmt nicht", obwohl alles stimmte.
// (b) Der Turnstile-Effekt darf bei `schritt === "code"` NICHT aussteigen.
//     Sonst räumt sein Aufräumer das Widget ab, und „Neuen Code schicken"
//     verschickt für immer dieselbe, verbrauchte Marke (Cloudflare:
//     timeout-or-duplicate) — was serverseitig jedes Mal eine Fehlzeile gegen
//     das Kontingent des ganzen Büros schreibt.
// (c) `pruefungZurueck()` läuft nach JEDEM Absenden, auch nach Erfolg: eine
//     Wertmarke gilt genau einmal.
// (d) Nur das ERSTE Codefeld trägt `autoComplete="one-time-code"` — iOS füllt
//     dort den ganzen Code ein, `codeSetzen()` verteilt ihn weiter.
//
// Der Behälter heißt `pruefung`, NIEMALS `turnstile`: der Browser legt für
// jedes Element mit id eine gleichnamige Eigenschaft auf `window` an, und
// `window.turnstile` wäre dann das <div> — die api.js hielte sich für schon
// geladen und zeichnete nichts (lib/turnstile.ts).
// ============================================================================
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Button, Icon, Input, Spinner, Switch } from "../components/ui";
import { Logo } from "../components/app/Logo";
import { sitzungVergessen } from "../components/app/TopNav";
import { ApiFehler } from "../api/client";
import { codeAnfordern, codeEinloesen, holeAuthConfig, holeMich, schluessel } from "../api/hooks";
import { CODE_LAENGE, codeRuecktaste, codeSetzen, codeText, codeVollstaendig, leererCode } from "../lib/code";
import { sekundenKurz } from "../lib/format";
import { useT } from "../lib/texte";
import { BEHAELTER_ID, ladeTurnstile } from "../lib/turnstile";
import { anmeldeZiel } from "../lib/wege";
import { useTitel } from "../hooks/useTitel";
import { useAuthStore } from "../store/useAuthStore";

type Schritt = "email" | "code";

const ERNEUT_SPERRE_S = 60;

export function Anmelden() {
  const { t } = useT();
  useTitel(t.anmelden.titel);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const status = useAuthStore((s) => s.status);
  const angemeldet = useAuthStore((s) => s.angemeldet);
  // Wohin nach dem Code (§32). `von` schreiben die Wächter in App.tsx schon
  // lange — gelesen hat es bis dahin niemand: wer einem Projektlink aus dem
  // Wochenbrief folgte, landete nach der Anmeldung auf „/" statt beim Projekt.
  // `von` kommt aus `history.state` und ist damit Eingabe; `anmeldeZiel`
  // lässt nur einen Pfad dieser Anwendung durch (lib/wege.ts).
  const ort = useLocation();
  const ziel = anmeldeZiel((ort.state as { von?: unknown } | null)?.von);

  const [schritt, setSchritt] = useState<Schritt>("email");
  const [email, setEmail] = useState("");
  // Vorbelegt AN: dieses Werkzeug wird täglich und auf dem Bau benutzt.
  const [bleiben, setBleiben] = useState(true);
  const [challenge, setChallenge] = useState("");
  const [code, setCode] = useState<string[]>(leererCode);
  const [fehler, setFehler] = useState("");
  const [laeuft, setLaeuft] = useState(false);
  const [wackelt, setWackelt] = useState(false);
  const [erneutAb, setErneutAb] = useState(0);
  const [jetzt, setJetzt] = useState(() => Date.now());

  const [sitekey, setSitekey] = useState<string | null>(null);
  const [domains, setDomains] = useState<string[]>([]);
  const [token, setToken] = useState<string | null>(null);
  const widgetRef = useRef<string | undefined>(undefined);

  // (a) Der Doppelabsende-Riegel. Ref, nicht Zustand.
  const laeuftRef = useRef(false);
  const feldRefs = useRef<(HTMLInputElement | null)[]>([]);

  /* --- Turnstile -------------------------------------------------------- */

  useEffect(() => {
    let lebt = true;
    holeAuthConfig()
      .then((c) => {
        if (!lebt) return;
        setSitekey(c.turnstile_sitekey);
        setDomains(c.allowed_domains ?? []);
      })
      .catch(() => {
        // Ohne Konfiguration bleibt der Knopf frei; scheitert die Prüfung
        // serverseitig, kommt der Grund als deutscher Satz zurück.
        if (lebt) setSitekey(null);
      });
    return () => {
      lebt = false;
    };
  }, []);

  // (b) Schritt 1 UND 2 brauchen ein lebendes Widget — `schritt` steht in der
  // Abhängigkeitsliste, damit im zweiten Schritt ein FRISCHES gezeichnet wird.
  useEffect(() => {
    if (!sitekey) return;
    let lebt = true;
    let id: string | undefined;
    ladeTurnstile()
      .then((api) => {
        if (!lebt || !document.getElementById(BEHAELTER_ID)) return;
        // Explizites render(): mit render=explicit zeichnet Cloudflare von
        // sich aus nichts mehr, und der Sitekey kommt ohnehin erst zur
        // Laufzeit aus GET /auth/config.
        id = api.render(`#${BEHAELTER_ID}`, {
          sitekey,
          action: "login",
          theme: "auto",
          callback: (tk) => setToken(tk),
          "error-callback": () => {
            setToken(null);
            setFehler(t.anmelden.pruefungFehler);
          },
          "expired-callback": () => setToken(null),
        });
        widgetRef.current = id;
      })
      .catch((e: unknown) => {
        if (lebt) setFehler(e instanceof Error ? e.message : t.anmelden.pruefungFehler);
      });
    return () => {
      lebt = false;
      if (id && window.turnstile) {
        try {
          window.turnstile.remove(id);
        } catch {
          /* schon weg */
        }
      }
      widgetRef.current = undefined;
    };
    // `t` gehört bewusst NICHT in die Liste: ein Sprachwechsel würde das
    // Widget sonst neu zeichnen und die gültige Wertmarke wegwerfen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sitekey, schritt]);

  /** (c) Nach JEDEM Absenden: die Wertmarke ist verbraucht. */
  const pruefungZurueck = useCallback(() => {
    setToken(null);
    if (widgetRef.current && window.turnstile) {
      try {
        window.turnstile.reset(widgetRef.current);
      } catch {
        /* Widget ist weg — dann gibt es auch nichts zurückzusetzen */
      }
    }
  }, []);

  /* --- Countdown „Neuen Code schicken" ---------------------------------- */

  useEffect(() => {
    if (!erneutAb) return;
    const uhr = setInterval(() => setJetzt(Date.now()), 500);
    return () => clearInterval(uhr);
  }, [erneutAb]);
  const restS = erneutAb ? Math.max(0, (erneutAb - jetzt) / 1000) : 0;

  /* --- Schritt 1: Code anfordern ---------------------------------------- */

  const anfordern = useCallback(
    async (erneut: boolean) => {
      if (laeuftRef.current) return;
      if (erneut && restS > 0) return;
      laeuftRef.current = true;
      setLaeuft(true);
      setFehler("");
      try {
        const a = await codeAnfordern(email.trim(), token);
        setChallenge(a.challenge);
        setCode(leererCode());
        setSchritt("code");
        setErneutAb(Date.now() + ERNEUT_SPERRE_S * 1000);
        setJetzt(Date.now());
        pruefungZurueck();
        setTimeout(() => feldRefs.current[0]?.focus(), 60);
      } catch (e) {
        setFehler(e instanceof ApiFehler ? e.detail : t.anmelden.startFehler);
        pruefungZurueck();
      } finally {
        laeuftRef.current = false;
        setLaeuft(false);
      }
    },
    [email, token, restS, pruefungZurueck, t],
  );

  /* --- Schritt 2: Code einlösen ----------------------------------------- */

  const absendenCode = useCallback(
    async (ziffern: string[]) => {
      if (laeuftRef.current) return;
      laeuftRef.current = true;
      setLaeuft(true);
      setFehler("");
      try {
        await codeEinloesen(challenge, codeText(ziffern), bleiben);
        // Fehlerrunde 01.10.2026, #38: VOR `angemeldet()` den Speicher der
        // vorigen Sitzung räumen. Lief sie ab (401), statt abgemeldet zu
        // werden, stand ihr `/stand` samt nicht freigegebener Projekte noch im
        // Cache und erschien der nächsten Person sofort. Jetzt hängt hier noch
        // kein geschützter Beobachter — die Schirme dahinter holen frisch.
        sitzungVergessen(qc);
        angemeldet();
        // Das Profil VOR dem Weiterspringen holen und ablegen: der Wächter in
        // App.tsx liest `profil_vollstaendig` aus genau diesem Eintrag, und
        // ohne ihn stünde die Karte einen Wimpernschlag lang ohne Person da.
        const ich = await qc.fetchQuery({ queryKey: schluessel.ich, queryFn: holeMich });
        // Der Profilriegel geht vor: ohne Namen und Büro erst das Profil, der
        // Verweis ist danach über den Link in der Mail wieder erreichbar.
        navigate(ich.profil_vollstaendig === false ? "/profil" : ziel, { replace: true });
      } catch (e) {
        setFehler(e instanceof ApiFehler ? e.detail : t.anmelden.codeFehler);
        setCode(leererCode());
        setWackelt(true);
        setTimeout(() => setWackelt(false), 320);
        setTimeout(() => feldRefs.current[0]?.focus(), 40);
      } finally {
        laeuftRef.current = false;
        setLaeuft(false);
      }
    },
    [challenge, bleiben, angemeldet, qc, navigate, t, ziel],
  );

  const ziffernSetzen = (index: number, roh: string) => {
    // Tippt jemand in ein schon gefülltes Kästchen, steht dort kurz „12".
    // Gemeint ist die NEUE Ziffer, nicht ein zweistelliger Einschub — sonst
    // rutscht die alte Ziffer nach rechts weiter.
    const alt = code[index] ?? "";
    const eingabe = alt && roh.length === 2 && roh.startsWith(alt) ? roh.slice(1) : roh;
    const { code: neu, fokus } = codeSetzen(code, index, eingabe);
    setCode(neu);
    feldRefs.current[fokus]?.focus();
    // Die sechste Ziffer schickt die Karte selbst ab.
    if (codeVollstaendig(neu)) void absendenCode(neu);
  };

  // Wer schon angemeldet ist, hat hier nichts verloren. Auch hier `ziel` und
  // nicht „/": dieser Effekt feuert nach dem Code ZUERST — `angemeldet()`
  // läuft vor dem `await` in `absendenCode` —, und mit „/" ginge der Verweis
  // genau in dem Moment verloren, für den `von` da ist.
  useEffect(() => {
    if (status === "angemeldet") navigate(ziel, { replace: true });
  }, [status, navigate, ziel]);

  // Der Rückfall ist DERSELBE Satzbauer mit den beiden Hausdomänen und nicht
  // ein zweiter, fest getippter Satz — der zweite Wortlaut wäre der, der beim
  // nächsten Mal vergessen wird.
  const domainHinweis = t.anmelden.domains(domains.length ? [...domains] : ["lilasp.de", "lilasp.gmbh"]);

  return (
    <main className="anmelde-huelle">
      <div className="anmelde-karte karte-ein spalte" style={{ gap: 18 }}>
        <Logo />

        {schritt === "email" ? (
          <form
            className="spalte"
            style={{ gap: 14 }}
            onSubmit={(ev) => {
              ev.preventDefault();
              void anfordern(false);
            }}
          >
            <div className="spalte" style={{ gap: 6 }}>
              <h1 className="seitentitel" style={{ fontSize: 28 }}>
                {t.anmelden.titel}
              </h1>
              <p className="unterzeile">{t.anmelden.unterzeile}</p>
            </div>

            <div>
              <label className="lbl" htmlFor="lp-email">
                {t.anmelden.email}
              </label>
              <Input
                id="lp-email"
                type="email"
                value={email}
                autoFocus
                required
                autoComplete="email"
                inputMode="email"
                placeholder={t.anmelden.emailPlatzhalter}
                onChange={(ev) => setEmail(ev.target.value)}
              />
              <p className="hinweiszeile" style={{ marginTop: 6 }}>
                {domainHinweis}
              </p>
            </div>

            <div className="reihe" style={{ justifyContent: "space-between", gap: 12 }}>
              <span style={{ fontSize: 13, fontWeight: 500 }}>{t.anmelden.bleiben}</span>
              <Switch on={bleiben} onChange={setBleiben} label={t.anmelden.bleiben} />
            </div>

            {/* (Behälter) pruefung, NIE turnstile. Ohne Sitekey bleibt er ohne
                Höhe und der Knopf ist frei. */}
            <div id={BEHAELTER_ID} style={{ minHeight: sitekey ? 68 : 0 }} />

            <p className="fehlerzeile" role="status">
              {fehler}
            </p>

            <Button type="submit" size="lg" style={{ width: "100%" }} disabled={laeuft || !email.trim() || (!!sitekey && !token)}>
              {laeuft && <Spinner size={14} color="currentColor" />}
              {t.anmelden.codeSchicken}
            </Button>
          </form>
        ) : (
          <form
            className={`spalte${wackelt ? " wackel" : ""}`}
            style={{ gap: 14 }}
            onSubmit={(ev) => {
              ev.preventDefault();
              if (codeVollstaendig(code)) void absendenCode(code);
            }}
          >
            <div className="spalte" style={{ gap: 6 }}>
              <h1 className="seitentitel" style={{ fontSize: 28 }}>
                {t.anmelden.codeTitel}
              </h1>
              <p className="unterzeile">{t.anmelden.codeUnterzeile(email.trim())}</p>
            </div>

            <div className="code-reihe">
              {code.map((z, i) => (
                <input
                  key={i}
                  ref={(el) => {
                    feldRefs.current[i] = el;
                  }}
                  className="codebox"
                  value={z}
                  inputMode="numeric"
                  // (d) Nur das erste Feld trägt one-time-code.
                  autoComplete={i === 0 ? "one-time-code" : "off"}
                  autoFocus={i === 0}
                  maxLength={CODE_LAENGE}
                  aria-label={t.anmelden.ziffer(i + 1, CODE_LAENGE)}
                  disabled={laeuft}
                  onChange={(ev) => ziffernSetzen(i, ev.target.value)}
                  onKeyDown={(ev) => {
                    if (ev.key === "Backspace") {
                      ev.preventDefault();
                      const { code: neu, fokus } = codeRuecktaste(code, i);
                      setCode(neu);
                      feldRefs.current[fokus]?.focus();
                    }
                    if (ev.key === "ArrowLeft") feldRefs.current[Math.max(0, i - 1)]?.focus();
                    if (ev.key === "ArrowRight") feldRefs.current[Math.min(CODE_LAENGE - 1, i + 1)]?.focus();
                  }}
                  onPaste={(ev) => {
                    ev.preventDefault();
                    ziffernSetzen(i, ev.clipboardData.getData("text"));
                  }}
                />
              ))}
            </div>

            <div className="reihe" style={{ gap: 9 }}>
              <span
                aria-hidden
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: "var(--r-knopf)",
                  background: bleiben ? "var(--accent)" : "var(--field)",
                  border: bleiben ? "none" : "1px solid var(--field-border)",
                  color: "#fff",
                  display: "grid",
                  placeItems: "center",
                  flex: "none",
                }}
              >
                {bleiben && <Icon name="check" size={13} />}
              </span>
              <span style={{ fontSize: 13 }}>{t.anmelden.bleiben}</span>
            </div>

            {/* Dasselbe <div> wie in Schritt 1 — immer nur EINES im Baum, sonst
                gäbe es die Kennung zweimal. Der Effekt oben zeichnet hierher
                ein frisches Widget, damit „Neuen Code schicken" eine
                unverbrauchte Marke hat (Falle b). */}
            <div id={BEHAELTER_ID} style={{ minHeight: sitekey ? 68 : 0 }} />

            <p className="fehlerzeile" role="status" style={{ textAlign: "center" }}>
              {fehler}
            </p>

            <Button type="submit" size="lg" style={{ width: "100%" }} disabled={laeuft || !codeVollstaendig(code)}>
              {laeuft && <Spinner size={14} color="currentColor" />}
              {t.anmelden.titel}
            </Button>

            <div className="reihe" style={{ justifyContent: "space-between", fontSize: 12 }}>
              <Button type="button" variant="ghost" size="sm" onClick={() => setSchritt("email")}>
                {t.anmelden.andereAdresse}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={restS > 0 || laeuft || (!!sitekey && !token)}
                onClick={() => void anfordern(true)}
              >
                {restS > 0 ? t.anmelden.erneutIn(sekundenKurz(restS)) : t.anmelden.erneut}
              </Button>
            </div>
          </form>
        )}

        <hr className="hr" />
        <p className="hinweiszeile">
          {domainHinweis} {t.anmelden.ohneAnmeldung}{" "}
          <Link to="/">{t.anmelden.zurKarte}</Link>
        </p>
      </div>
    </main>
  );
}
